import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { Alert } from '@app/ui/alert';
import { Label } from '@app/ui/label';
import { Tooltip } from '@app/ui/tooltip';
import { Loader } from '@app/ui/loader';

import {
  COLOR_RUBRIC_PREVIEW_GET_ONE,
  COLOR_SCORING_RULE_DELETE_ONE,
  COLOR_GATE_DELETE_ONE,
  FIELD_CATALOGUE_GET_MANY,
} from '../../gql';
import {
  ColorGate,
  ColorRubricPreview,
  ColorScoringRule,
  RuleField,
  RuleFamily,
  RuleSetVersion,
  RuleType,
} from '../../types';
import { ColorScoringRuleEditor } from './ColorScoringRuleEditor';
import { ColorGateEditor } from './ColorGateEditor';
import { ColorBandsEditor } from './ColorBandsEditor';
import { ColorItemDeleteDialog } from './ColorItemDeleteDialog';

export interface ColorRubricPanelProps {
  readonly version: RuleSetVersion;
  readonly editable: boolean;
  readonly onChanged: () => Promise<unknown> | void;
}

const NO_FIELDS: RuleField[] = [];

const formatBandRange = (min: number | null, max: number | null): string => {
  if (min == null) {
    return `≤ ${max}`;
  }

  if (max == null) {
    return `≥ ${min}`;
  }

  return min === max ? String(min) : `${min}–${max}`;
};

// The Color coding tab's three sections (SPEC §13) — replaces the watchlist's single `RuleList`
// inside `FamilyRulesPanel`'s frame. Row counts are tiny (a handful of scoring rules/gates/bands),
// so this is plain rows, not the shared DataGrid `Table` (its row-height/padding traps only matter
// at DataGrid scale — `frontend-ui.md`).
export const ColorRubricPanel = ({ version, editable, onChanged }: ColorRubricPanelProps) => {
  const { t } = useTranslation('common');

  const [editingRule, setEditingRule] = useState<ColorScoringRule | 'new' | null>(null);
  const [deletingRule, setDeletingRule] = useState<ColorScoringRule | null>(null);
  const [editingGate, setEditingGate] = useState<ColorGate | 'new' | null>(null);
  const [deletingGate, setDeletingGate] = useState<ColorGate | null>(null);
  const [bandsOpen, setBandsOpen] = useState(false);

  const { data: catalogueData } = useQuery<{ fieldCatalogueGetMany: RuleField[] }>(FIELD_CATALOGUE_GET_MANY, {
    variables: { family: RuleFamily.colorCoding },
  });
  const fields = catalogueData?.fieldCatalogueGetMany ?? NO_FIELDS;
  const fieldsByPath = useMemo(() => new Map(fields.map(field => [field.path, field])), [fields]);

  const {
    data: previewData,
    loading: previewLoading,
    refetch: refetchPreview,
  } = useQuery<{ colorRubricPreviewGetOne: ColorRubricPreview }>(COLOR_RUBRIC_PREVIEW_GET_ONE, {
    variables: { ruleSetVersionId: version.id },
    fetchPolicy: 'cache-and-network',
  });
  const preview = previewData?.colorRubricPreviewGetOne;

  const scoringRules = [...(version.colorScoringRules ?? [])].sort((a, b) => a.ordinal - b.ordinal);
  const gates = [...(version.colorGates ?? [])].sort((a, b) => a.ordinal - b.ordinal);
  const bands = [...(version.colorBands ?? [])].sort((a, b) => a.ordinal - b.ordinal);

  const refresh = async () => {
    await Promise.all([onChanged(), refetchPreview()]);
  };

  return (
    <Box>
      {/* PLAN P16/AC-5b — the SAME server-authored text as the publish dialog, rendered here too
      (one string from one place, `validate-color-rubric.ts`, never re-derived on the client). */}
      {!previewLoading && preview && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('rules.colorCoding.preview.rangeLabel', { min: preview.minScore, max: preview.maxScore })}
          </Typography>

          {preview.blocking.map(message => (
            <Alert key={message} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {preview.warnings.map(message => (
            <Alert key={message} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}

      {/* ── Scoring rules ──────────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h2">{t('rules.colorCoding.scoringRule.sectionTitle')}</Typography>
        {editable && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEditingRule('new')}>
            {t('rules.colorCoding.scoringRule.addButton')}
          </Button>
        )}
      </Box>

      {!scoringRules.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('rules.colorCoding.scoringRule.empty')}
        </Typography>
      )}

      {scoringRules.map(rule => (
        <Box
          key={rule.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
            {rule.ruleId}
          </Typography>
          <Label
            text={t(`rules.type.option.${rule.type}`, rule.type)}
            color={rule.type === RuleType.ai ? 'info' : 'grey'}
            sx={{ lineHeight: 'normal' }}
          />
          <Label
            text={rule.enabled ? t('general.option.yes') : t('general.option.no')}
            color={rule.enabled ? 'success' : 'grey'}
            sx={{ lineHeight: 'normal' }}
          />
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {rule.ruleText}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={theme => ({ width: theme.spacing(34), textAlign: 'left', flexShrink: 0 })}
          >
            {rule.type === RuleType.ai
              ? t('rules.colorCoding.scoringRule.aiSummary', { count: rule.points ?? 0 })
              : t('rules.colorCoding.scoringRule.exactSummary', {
                  field: fieldsByPath.get(rule.field ?? '')?.label ?? rule.field,
                  count: rule.cases.length,
                })}
          </Typography>

          {editable && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={t('rules.list.editTooltip') ?? undefined}>
                <IconButton size="small" onClick={() => setEditingRule(rule)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('rules.delete.button') ?? undefined}>
                <IconButton size="small" color="error" onClick={() => setDeletingRule(rule)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      {/* ── Gates ──────────────────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h2">{t('rules.colorCoding.gate.sectionTitle')}</Typography>
        {editable && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEditingGate('new')}>
            {t('rules.colorCoding.gate.addButton')}
          </Button>
        )}
      </Box>

      {!gates.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('rules.colorCoding.gate.empty')}
        </Typography>
      )}

      {gates.map(gate => (
        <Box
          key={gate.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
            {gate.gateId}
          </Typography>
          <Label
            text={t(`rules.colorCoding.gate.kind.option.${gate.kind}`, gate.kind)}
            color={gate.kind === 'override' ? 'warning' : 'info'}
            sx={{ lineHeight: 'normal' }}
          />
          <Label
            text={t(`rules.colorCoding.color.option.${gate.color}`, gate.color)}
            color="grey"
            sx={{ lineHeight: 'normal' }}
          />
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {gate.ruleText}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={theme => ({ width: theme.spacing(34), textAlign: 'left', flexShrink: 0 })}
          >
            {t('rules.colorCoding.gate.orderLabel', { order: gate.order })}
          </Typography>

          {editable && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={t('rules.list.editTooltip') ?? undefined}>
                <IconButton size="small" onClick={() => setEditingGate(gate)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('rules.delete.button') ?? undefined}>
                <IconButton size="small" color="error" onClick={() => setDeletingGate(gate)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      {/* ── Bands ──────────────────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h2">{t('rules.colorCoding.bands.sectionTitle')}</Typography>
        {editable && (
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setBandsOpen(true)}>
            {t('rules.colorCoding.bands.editButton')}
          </Button>
        )}
      </Box>

      {!bands.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('rules.colorCoding.bands.empty')}
        </Typography>
      )}

      {bands.map(band => (
        <Box key={band.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Label
            text={t(`rules.colorCoding.color.option.${band.color}`, band.color)}
            color="grey"
            sx={{ lineHeight: 'normal' }}
          />
          <Typography variant="body1">{formatBandRange(band.min, band.max)}</Typography>
        </Box>
      ))}

      {editingRule && (
        <ColorScoringRuleEditor
          open
          onClose={() => setEditingRule(null)}
          ruleSetVersionId={version.id}
          rule={editingRule === 'new' ? null : editingRule}
          fields={fields}
          onSaved={refresh}
        />
      )}

      {deletingRule && (
        <ColorItemDeleteDialog
          open
          onClose={() => setDeletingRule(null)}
          mutation={COLOR_SCORING_RULE_DELETE_ONE}
          id={deletingRule.id}
          itemText={deletingRule.ruleText}
          dialogTitle={t('rules.delete.dialogTitle', { ruleId: deletingRule.ruleId }) ?? ''}
          successMessage={t('rules.delete.successMessage', { ruleId: deletingRule.ruleId }) ?? ''}
          onDeleted={async () => {
            setDeletingRule(null);
            await refresh();
          }}
        />
      )}

      {editingGate && (
        <ColorGateEditor
          open
          onClose={() => setEditingGate(null)}
          ruleSetVersionId={version.id}
          gate={editingGate === 'new' ? null : editingGate}
          fields={fields}
          onSaved={refresh}
        />
      )}

      {deletingGate && (
        <ColorItemDeleteDialog
          open
          onClose={() => setDeletingGate(null)}
          mutation={COLOR_GATE_DELETE_ONE}
          id={deletingGate.id}
          itemText={deletingGate.ruleText}
          dialogTitle={t('rules.delete.dialogTitle', { ruleId: deletingGate.gateId }) ?? ''}
          successMessage={t('rules.delete.successMessage', { ruleId: deletingGate.gateId }) ?? ''}
          onDeleted={async () => {
            setDeletingGate(null);
            await refresh();
          }}
        />
      )}

      {bandsOpen && (
        <ColorBandsEditor
          open
          onClose={() => setBandsOpen(false)}
          ruleSetVersionId={version.id}
          bands={bands}
          onSaved={refresh}
        />
      )}

      <Loader isLoading={previewLoading && !previewData} />
    </Box>
  );
};
